import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { traceSpan } from "@/lib/sentry-spans";
import {
  insertTemplateLinesForOffer,
  insertDefaultLinesForOffer,
  upsertOfferWritingLine,
} from "./use-offer-line-mutations";
import type { Offer, OfferLine } from "./use-offers";
import { calcOfferTotals } from "@/lib/offer-calc";
import { toCalcLines } from "@/lib/offer-edit-calc";

// Re-eksporter pure helpers og linje-mutasjoner fra deres nye hjem slik at
// eksisterende importstier (`@/hooks/use-offers` barrel + `./use-offer-mutations`)
// fortsetter å virke uendret etter WP10 GJELD-2-splittet.
export {
  computeOfferWritingHours,
  includeDefaultLine,
} from "@/lib/offer-mutation-helpers";
export {
  insertTemplateLinesForOffer,
  insertDefaultLinesForOffer,
  upsertOfferWritingLine,
  useCreateOfferLine,
  useUpdateOfferLine,
  useDeleteOfferLine,
  useAddOfferTemplateLines,
} from "./use-offer-line-mutations";

interface CreateOfferInput {
  title: string;
  customer_id?: string | null;
  customer_name?: string | null;
  description?: string | null;
  /** Optional offer template codes to populate initial lines (VIB-553). */
  template_codes?: string[];
  /** Hvis false skjules Forbehold/faktureringsvilkår i kopier-bilde og Keynote. */
  show_invoice_terms?: boolean;
  /** Faktureringsterminer (default = 60/40 hvis utelatt). */
  invoice_terms?: Array<{ label: string; pct: number }>;
  /** VIB-663a: Rut auto-tilbudsskriving-linja til Konseptutvikling-malen. Default false. */
  concept_routing?: boolean;
  /** VIB-993: reise faktureres separat (medgått). Default false. */
  bills_travel_per_medgatt?: boolean;
  /**
   * Overheadpåslag for tilbudet. Hvis utelatt brukes
   * `system_config.default_overhead_pct` (VIB-706).
   */
  overhead_pct?: number;
  /** VIB-1136: 'import' dropper auto-linjer + lagres på raden. Default 'manual'. */
  source?: "manual" | "import";
  /** CRM-2.7: prosessen tilbudet lages fra; en DB-trigger flytter den til Tilbud. */
  opportunity_id?: string | null;
}

/**
 * VIB-2532: skriv `total_amount` fra de faktiske linjene rett etter
 * opprettelse, så et nytt tilbud ikke er «ulagret» fra første sekund.
 *
 * Byggeren regner `canSave = isDirty || offer.total_amount !== totals.total`
 * (use-offer-edit-state.ts). Uten denne synken står raden med 0 mens
 * auto-linjene (Reisetid, Tilbudsskriving) gir en sum > 0: Lagre-knappen
 * lyser, «Dupliser tilbud» er sperret og lista viser 0 kr til første Lagre.
 *
 * Bruker SAMME formel som byggeren: calcOfferTotals over hovedlinjer
 * (uten section_id, VIB-663e) via toCalcLines. Ikke lag en egen sum her.
 */
export async function syncOfferTotalAmount(offer: Offer): Promise<number> {
  // Bundet på offer_id: et tilbud har langt under 1000 linjer.
  const { data: lines, error: linesErr } = await supabase
    .from("offer_lines")
    .select("*")
    .eq("offer_id", offer.id);
  if (linesErr) throw linesErr;

  const mainLines = ((lines ?? []) as OfferLine[]).filter((l) => !l.section_id);
  const { total } = calcOfferTotals(
    {
      overhead_pct: offer.overhead_pct,
      discount_pct: offer.discount_pct,
      discount_amount: offer.discount_amount,
      include_travel_in_overhead: offer.include_travel_in_overhead,
    },
    toCalcLines(mainLines),
  );

  const { error: updErr } = await supabase
    .from("offers")
    .update({ total_amount: total })
    .eq("id", offer.id);
  if (updErr) throw updErr;
  return total;
}

export function useCreateOffer() {
  const queryClient = useQueryClient();
  const { activeUser } = useImpersonation();

  return useMutation({
    mutationFn: async (input: CreateOfferInput): Promise<Offer> =>
      // VIB-642: instrumenter hele offer.create-flyten som ett span, slik
      // at Sentry Performance kan vise hvor lang tid template-insert +
      // default-linjer + auto-linje tar samlet. Indre supabase-kall
      // dukker opp som child-spans via browserTracing.
      traceSpan("offer.create", "db.mutation", async () => {
      if (!activeUser?.id) throw new Error("Ingen aktiv bruker");

      // Hent default overheadpåslag fra system_config (VIB-706). Faller
      // tilbake til 15 hvis nøkkelen mangler, så nye tilbud aldri opprettes
      // med ugyldig verdi. Caller kan overstyre via input.overhead_pct.
      let resolvedOverhead = input.overhead_pct;
      if (resolvedOverhead == null) {
        const { data: overheadRow } = await supabase
          .from("system_config")
          .select("value")
          .eq("key", "default_overhead_pct")
          .maybeSingle();
        const parsed = Number(overheadRow?.value);
        resolvedOverhead = Number.isFinite(parsed) ? parsed : 15;
      }

      const { data: offer, error: offerErr } = await supabase
        .from("offers")
        .insert({
          title: input.title,
          customer_id: input.customer_id ?? null,
          customer_name: input.customer_name ?? null,
          description: input.description ?? null,
          created_by: activeUser.id,
          overhead_pct: resolvedOverhead,
          writers: [{ user_id: activeUser.id, share_pct: 100 }],
          ...(input.show_invoice_terms !== undefined
            ? { show_invoice_terms: input.show_invoice_terms }
            : {}),
          ...(input.invoice_terms !== undefined
            ? { invoice_terms: input.invoice_terms }
            : {}),
          ...(input.concept_routing !== undefined
            ? { concept_routing: input.concept_routing }
            : {}),
          ...(input.bills_travel_per_medgatt
            ? { bills_travel_per_medgatt: true }
            : {}),
          ...(input.source ? { source: input.source } : {}),
          ...(input.opportunity_id ? { opportunity_id: input.opportunity_id } : {}),
        })
        .select()
        .single();
      if (offerErr) throw offerErr;

      // Hent system-timesats først så både template-linjer og default-linjer
      // bruker samme verdi (en endring i innstillinger slår gjennom på alle nye
      // tilbud).
      const { data: rateRow } = await supabase
        .from("system_config")
        .select("value")
        .eq("key", "hourly_rate")
        .maybeSingle();
      const hourlyRate = Number(rateRow?.value) || 2000;

      if (input.template_codes && input.template_codes.length > 0) {
        await insertTemplateLinesForOffer(offer.id, input.template_codes, 0);
      }

      if (input.source !== "import") {
        // Auto-default linjer (f.eks. Reisetid 4t) — alltid på nye tilbud,
        // bruker fjerner manuelt hvis ikke aktuelt. VIB-1054: reise-defaultlinja
        // droppes når reise faktureres separat (medgått).
        // VIB-1136: importerte tilbud er en nøyaktig refleksjon av kilden
        // og får ingen auto-linjer.
        await insertDefaultLinesForOffer(
          offer.id,
          hourlyRate,
          !!input.bills_travel_per_medgatt,
        );

        // Auto-linje for tilbudsskriving — opprettes med qty=0 ved create,
        // oppdateres automatisk når BF endres (i save-handler i editor).
        await upsertOfferWritingLine({
          offerId: offer.id,
          bfAmount: 0,
          hourlyRate,
          isFrozen: false,
          conceptRouting: input.concept_routing ?? false,
        });
      }

      // VIB-2532: synk total_amount med linjene som nettopp ble satt inn.
      const total_amount = await syncOfferTotalAmount(offer as Offer);
      return { ...(offer as Offer), total_amount };
    }),
    onSuccess: async (offer) => {
      await invalidateFor(queryClient, "offers", { offerId: offer.id });
      await invalidateFor(queryClient, "offer_lines", { offerId: offer.id });
      // VIB-1184: offer.created logges autoritativt av DB-triggeren trg_log_offer_change.
    },
  });
}

export function useUpdateOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      patch: Partial<
        Pick<
          Offer,
          | "title"
          | "description"
          | "customer_id"
          | "customer_name"
          | "status"
          | "overhead_pct"
          | "discount_pct"
          | "discount_amount"
          | "startup_invoice_pct"
          | "final_invoice_pct"
          | "total_amount"
          | "hourly_rate"
          | "show_invoice_terms"
          | "invoice_terms"
          | "custom_terms_text"
          | "concept_routing"
          | "writers"
          | "bills_travel_per_medgatt"
          | "sent_at"
          | "accepted_at"
          | "probability"
          | "valid_until"
          | "expected_close_date"
          | "lost_at"
          | "lost_reason"
        >
      >;
    }): Promise<void> => {
      const { error } = await supabase
        .from("offers")
        .update(params.patch)
        .eq("id", params.id);
      if (error) throw error;
    },
    // Optimistisk oppdatering: UI reflekterer status/felt-endringer
    // umiddelbart i stedet for å vente på Supabase round-trip (kan ta
    // flere sekunder mot prod-DB). Avhengige UI-elementer (Konverter-
    // knappen, status-badge) blir synlige med en gang.
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["offer", variables.id] });
      const previous = queryClient.getQueryData<Offer>(["offer", variables.id]);
      if (previous) {
        queryClient.setQueryData<Offer>(["offer", variables.id], {
          ...previous,
          ...variables.patch,
        });
      }
      return { previous };
    },
    onError: (_err, variables, context) => {
      // Rull tilbake hvis serveren avviste oppdateringen.
      if (context?.previous) {
        queryClient.setQueryData(["offer", variables.id], context.previous);
      }
    },
    onSuccess: async (_, variables) => {
      await invalidateFor(queryClient, "offers", { offerId: variables.id });
      // VIB-1184: offer.status_changed/offer.updated logges autoritativt (med
      // snapshot) av DB-triggeren trg_log_offer_change.
    },
  });
}

export function useDeleteOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from("offers").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async (_, id) => {
      await invalidateFor(queryClient, "offers", { offerId: id });
      // VIB-1184: offer.deleted logges autoritativt MED snapshot av DB-triggeren
      // trg_log_offer_change (frontend-logging hadde null payload).
    },
  });
}
